import { type ComponentPropsWithoutRef, useState } from 'react';
import { removeUser } from '../../sources/organization';
import translations from '../../translations';
import { resolveOrgUserRole } from '../../utils/organization';
import { stringReplace } from '../../utils/string';
import ConfirmModal from '../generic/modal/ConfirmModal';
import { useNotificationDispatch } from '../notifications/NotificationProvider';
import { useTenant } from '../TenantProvider';
import { useUser, useUserDispatch } from '../UserProvider';
import { getUser } from '../../sources/user';

type LeaveOrgProps = {
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
    fetchUsers: () => Promise<void>;
} & Pick<ComponentPropsWithoutRef<typeof ConfirmModal>, 'container'>;

const LeaveOrg = ({ fetchUsers, container }: LeaveOrgProps) => {
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const user = useUser();
    const dispatchUser = useUserDispatch();

    const tenant = useTenant();
    const dispatchNotification = useNotificationDispatch();

    const handleShowModal = () => {
        setShowModal(true);
    };

    const handleHideModal = () => {
        setShowModal(false);
    };

    const handleLeaveConfirm = async () => {
        try {
            setIsLoading(true);

            await removeUser(user.id);

            // Reload the current user in case they have been removed from the current organization
            const freshUser = await getUser();
            dispatchUser({ type: 'setUser', payload: freshUser });

            if (resolveOrgUserRole(user, tenant).isUserAdmin) {
                await fetchUsers();
            }

            dispatchNotification({
                type: 'notifySuccess',
                payload: {
                    message: stringReplace(translations.FORG_left_confirmation, {
                        user: user.email,
                    }),
                },
            });

            handleHideModal();
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <button className="btn btn-danger" onClick={handleShowModal} type="button">
                {translations.FORG_leave_organization_button_label}
            </button>
            <ConfirmModal
                show={showModal}
                onCancel={handleHideModal}
                onConfirm={handleLeaveConfirm}
                title={translations.FORG_leave_confirmation_title}
                messages={[translations.FORG_leave_confirmation]}
                container={container}
                isInProgress={isLoading}
            />
        </>
    );
};

export default LeaveOrg;
